/**
 * O reconhecimento dos provedores de boleto e a montagem do lançamento de conferência.
 *
 * Papel: aqui ficam `reconhecerProvedor`, que acha o provedor pelo texto do PDF,
 * `montarLancamentoDeBoleto`, que junta a leitura genérica de `lerBoleto` com o
 * favorecido, a descrição, a categoria e a observação do provedor, e
 * `CATEGORIA_POR_PROVEDOR`, a categoria e a subcategoria que cada provedor sugere.
 *
 * Como usar: `montarLancamentoDeBoleto` recebe o `BoletoLido` de `lerBoleto` e as
 * mesmas linhas do PDF, nunca uma leitura feita por outra via. Um campo que a
 * leitura não trouxe volta `null` ou com aviso, e a conferência o deixa vazio ou
 * marcado. Os provedores só acrescentam informação: quem não é reconhecido ganha a
 * descrição genérica "Boleto" e nenhuma categoria, e o usuário completa na
 * conferência.
 *
 * As categorias são os nomes exatos do plano de contas de despesa do caixa. Trocar
 * uma delas aqui muda só a sugestão; o lançamento gravado passa a usar o que o
 * usuário confirmar na conferência.
 *
 * Notas: o CPFL não tem código de barras (é DANF3E), então valor e vencimento vêm
 * do texto ("Total a Pagar" e a primeira data depois de "Vencimento"), e o aviso
 * de conferência é obrigatório. Os nomes de favorecido são os que o documento
 * imprime como beneficiário, sem CNPJ, porque é por eles que a conferência procura
 * o favorecido já cadastrado.
 */
import { numeroDoPdf } from './folha-pdf';
import type { BoletoLido } from './boleto-pdf';

/** Os provedores que o reconhecimento sabe identificar. */
export type ProvedorDeBoleto = 'claro' | 'hubert' | 'voltera' | 'ultragaz' | 'cpfl';

/** Uma categoria e subcategoria do plano de contas de despesa. */
export interface CategoriaSugerida {
  categoria: string;
  subcategoria: string;
}

/** O lançamento que a conferência de boletos mostra antes de gravar. */
export interface BoletoParaConferencia {
  favorecidoNome: string | null;
  descricao: string;
  categoria: string | null;
  subcategoria: string | null;
  valor: number | null;
  /** O vencimento em `AAAA-MM-DD`, ou `null`. */
  vencimento: string | null;
  observacao: string | null;
  avisos: string[];
}

/** A categoria que cada provedor sugere, com os nomes do plano de contas. */
export const CATEGORIA_POR_PROVEDOR: Readonly<Record<ProvedorDeBoleto, CategoriaSugerida>> = {
  claro: { categoria: 'Fixas', subcategoria: 'Telefonia celular' },
  hubert: { categoria: 'Variáveis', subcategoria: 'Associação BV - Condomínio' },
  voltera: { categoria: 'Variáveis', subcategoria: 'Consumo de energia elétrica - Voltera' },
  ultragaz: { categoria: 'Variáveis', subcategoria: 'Consumo de gás' },
  cpfl: { categoria: 'Variáveis', subcategoria: 'Consumo de energia elétrica - CPFL' },
};

/** O nome do favorecido e a descrição de cada provedor. */
const IDENTIDADE_POR_PROVEDOR: Readonly<
  Record<ProvedorDeBoleto, { favorecido: string; descricao: string }>
> = {
  claro: { favorecido: 'Claro', descricao: 'Claro' },
  hubert: { favorecido: 'ASSOC PART COMPL FAZENDA BOA VISTA', descricao: 'Condomínio BV' },
  voltera: {
    favorecido: 'VOLTERA COMERCIALIZADORA DE ENERGIA LTDA.',
    descricao: 'Voltera (energia)',
  },
  ultragaz: { favorecido: 'COMPANHIA ULTRAGAZ S A', descricao: 'Ultragaz (gás)' },
  cpfl: { favorecido: 'COMPANHIA PIRATININGA DE FORÇA E LUZ', descricao: 'CPFL (energia)' },
};

/**
 * O texto que identifica cada provedor, na ordem em que o reconhecimento os testa.
 *
 * "Claro" fica por último e sem `i`, porque a palavra "claro" em minúscula aparece
 * em frases comuns e não pode virar a operadora.
 */
const PADROES: ReadonlyArray<{ provedor: ProvedorDeBoleto; padrao: RegExp }> = [
  {
    provedor: 'hubert',
    padrao: /ASSOC PART COMPL FAZENDA BOA VISTA|RECIBO DE CONDOM[ÍI]NIO/i,
  },
  { provedor: 'voltera', padrao: /VOLTERA/i },
  { provedor: 'ultragaz', padrao: /ULTRAGAZ/i },
  { provedor: 'cpfl', padrao: /PIRATININGA DE FOR[ÇC]A E LUZ|\bCPFL\b/i },
  { provedor: 'claro', padrao: /\bClaro\b|\bCLARO\b/ },
];

/** Um item do recibo de condomínio: texto em maiúsculas e um valor no fim da linha. */
const ITEM_DO_RECIBO = /^([A-ZÀ-Ý].*?)\s+(\d{1,3}(?:\.\d{3})*,\d{2})$/;

/** O "Total a Pagar" do CPFL, com o valor depois dele. */
const TOTAL_A_PAGAR = /Total a Pagar\D*?(\d{1,3}(?:\.\d{3})*,\d{2})/i;

/** Data `DD/MM/AAAA`. */
const PADRAO_DATA = /(\d{2})\/(\d{2})\/(\d{4})/;

/** O aviso do CPFL, que não tem código de barras para conferir o valor. */
const AVISO_SEM_CODIGO =
  'Sem código de barras no documento: confira valor e vencimento com a nota antes de confirmar.';

/**
 * Descobre qual provedor emitiu o boleto, pelo texto do PDF.
 *
 * Quem chama é a importação de boleto, antes de montar o lançamento. Como o texto
 * inteiro é procurado, o nome do provedor pode estar em qualquer linha, e a
 * primeira regra da lista que casa é a que vale.
 *
 * @param linhas - As linhas de texto do PDF, na ordem em que aparecem.
 * @returns O provedor reconhecido, ou `null` quando nenhum padrão casa.
 */
export function reconhecerProvedor(linhas: string[]): ProvedorDeBoleto | null {
  const texto = linhas.join('\n');
  const encontrado = PADROES.find(({ padrao }) => padrao.test(texto));
  return encontrado?.provedor ?? null;
}

/**
 * Monta o lançamento de conferência a partir da leitura do boleto e do provedor.
 *
 * Quem chama é a importação de boleto, uma vez por arquivo, depois de `lerBoleto`.
 * Para o CPFL, valor e vencimento não vêm do código e são lidos do texto; para os
 * demais, valem os de `lido`, sem troca. A observação traz o banco do código e,
 * no recibo de condomínio, os itens legíveis, porque o lançamento é um só pelo
 * total do código.
 *
 * @param lido - O resultado de `lerBoleto` para este arquivo.
 * @param linhas - As mesmas linhas do PDF que `lerBoleto` recebeu.
 * @returns O lançamento com o favorecido, a categoria e os avisos de cada campo.
 */
export function montarLancamentoDeBoleto(
  lido: BoletoLido,
  linhas: string[],
): BoletoParaConferencia {
  const provedor = reconhecerProvedor(linhas);
  const sugestao = provedor === null ? null : CATEGORIA_POR_PROVEDOR[provedor];
  const identidade = provedor === null ? null : IDENTIDADE_POR_PROVEDOR[provedor];

  if (provedor === 'cpfl') {
    return montarCpfl(linhas, sugestao, identidade);
  }

  return {
    favorecidoNome: identidade?.favorecido ?? null,
    descricao: identidade?.descricao ?? 'Boleto',
    categoria: sugestao?.categoria ?? null,
    subcategoria: sugestao?.subcategoria ?? null,
    valor: lido.valor,
    vencimento: lido.vencimento,
    observacao: montarObservacao(lido, provedor, linhas),
    avisos: lido.avisos,
  };
}

/**
 * Monta o lançamento do CPFL, que não tem código para dar valor e vencimento.
 *
 * Quem chama é `montarLancamentoDeBoleto`, só para o provedor `cpfl`. Os avisos
 * da leitura do código são trocados pelo aviso de conferência, porque a falta de
 * código é o fato que importa para o usuário.
 */
function montarCpfl(
  linhas: string[],
  sugestao: CategoriaSugerida | null,
  identidade: { favorecido: string; descricao: string } | null,
): BoletoParaConferencia {
  const total = TOTAL_A_PAGAR.exec(linhas.join('\n'));
  const valor = total === null ? null : numeroDoPdf(total[1] ?? '');
  const avisos = [AVISO_SEM_CODIGO];
  if (valor === null) {
    avisos.push('Total a Pagar não encontrado no texto; preencha o valor na conferência.');
  }
  return {
    favorecidoNome: identidade?.favorecido ?? null,
    descricao: identidade?.descricao ?? 'Boleto',
    categoria: sugestao?.categoria ?? null,
    subcategoria: sugestao?.subcategoria ?? null,
    valor,
    vencimento: vencimentoDoTexto(linhas),
    observacao: null,
    avisos,
  };
}

/**
 * Escreve a observação do lançamento: o banco do código e, no recibo de condomínio, os itens.
 *
 * Quem chama é `montarLancamentoDeBoleto`. Só o provedor `hubert` tem itens; os
 * demais saem apenas com o banco. Quando não há nem banco nem itens, a observação
 * é `null`, e a conferência não mostra o campo.
 */
function montarObservacao(
  lido: BoletoLido,
  provedor: ProvedorDeBoleto | null,
  linhas: string[],
): string | null {
  const partes: string[] = [];
  if (lido.banco !== null) partes.push(`Banco: ${lido.banco}`);
  if (provedor === 'hubert') {
    const itens = itensDoRecibo(linhas);
    if (itens.length > 0) partes.push(`Itens: ${itens.join('; ')}`);
  }
  return partes.length === 0 ? null : partes.join('. ');
}

/**
 * Os itens do recibo de condomínio, uma linha por item, com o valor no fim.
 *
 * Linhas de total e de vencimento não são itens e ficam de fora, mesmo que
 * terminem em valor. Os espaços repetidos são colapsados para a observação caber
 * numa linha.
 */
function itensDoRecibo(linhas: string[]): string[] {
  return linhas
    .map((linha) => linha.replace(/\s+/g, ' ').trim())
    .filter((linha) => ITEM_DO_RECIBO.test(linha) && !/TOTAL|VALOR|VENCIMENTO/i.test(linha));
}

/**
 * Acha a primeira data válida na linha de "Vencimento" ou na seguinte.
 *
 * Quem chama é `montarCpfl`, porque o CPFL não traz o vencimento no código. Uma
 * data que não existe, como 31/02, não conta.
 *
 * @returns A data em `AAAA-MM-DD`, ou `null` quando nenhuma data válida está perto.
 */
function vencimentoDoTexto(linhas: string[]): string | null {
  for (const [indice, linha] of linhas.entries()) {
    if (!/vencimento/i.test(linha)) continue;
    const data = PADRAO_DATA.exec(`${linha} ${linhas[indice + 1] ?? ''}`);
    if (data === null) continue;
    const dia = Number(data[1]);
    const mes = Number(data[2]);
    const ano = Number(data[3]);
    const existe = new Date(Date.UTC(ano, mes - 1, dia));
    if (existe.getUTCDate() === dia && existe.getUTCMonth() === mes - 1) {
      return `${data[3] ?? ''}-${data[2] ?? ''}-${data[1] ?? ''}`;
    }
  }
  return null;
}
